/**
 * Currency context provider for multi-currency support
 * Fetches dynamic exchange rates from exchangerate-api.com
 * Caches rates for 1 hour to minimize API calls
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { Currency } from '../types';

/**
 * Currency display configuration
 */
interface CurrencyConfig {
  symbol: string;
  code: Currency;
  name: string;
  locale: string;
}

/**
 * Currency configurations with symbols and locales
 */
export const CURRENCY_CONFIG: Record<Currency, CurrencyConfig> = {
  [Currency.GBP]: { symbol: '£', code: Currency.GBP, name: 'British Pound', locale: 'en-GB' },
  [Currency.EUR]: { symbol: '€', code: Currency.EUR, name: 'Euro', locale: 'de-DE' },
  [Currency.USD]: { symbol: '$', code: Currency.USD, name: 'US Dollar', locale: 'en-US' },
  [Currency.CAD]: { symbol: 'C$', code: Currency.CAD, name: 'Canadian Dollar', locale: 'en-CA' },
  [Currency.AUD]: { symbol: 'A$', code: Currency.AUD, name: 'Australian Dollar', locale: 'en-AU' },
};

/**
 * Fallback exchange rates (GBP as base) if API fails
 * Updated periodically - these are approximate rates
 */
const FALLBACK_RATES: Record<Currency, number> = {
  [Currency.GBP]: 1,
  [Currency.EUR]: 1.17,
  [Currency.USD]: 1.27,
  [Currency.CAD]: 1.72,
  [Currency.AUD]: 1.93,
};

/**
 * Cache TTL in milliseconds (1 hour)
 */
const CACHE_TTL = 60 * 60 * 1000;

/**
 * LocalStorage keys
 */
const STORAGE_KEYS = {
  currency: 'foxie_currency',
  rates: 'foxie_exchange_rates',
  ratesTimestamp: 'foxie_exchange_rates_timestamp',
};

interface CurrencyContextValue {
  /** Currently selected currency */
  currency: Currency;
  /** Set the selected currency */
  setCurrency: (currency: Currency) => void;
  /** Exchange rates relative to GBP (base) */
  exchangeRates: Record<Currency, number>;
  /** Whether rates are currently being fetched */
  loading: boolean;
  /** Last error from API fetch */
  error: string | null;
  /** Convert an amount from one currency to the selected currency */
  convertPrice: (amount: number, fromCurrency?: Currency | string) => number;
  /** Format a price in the selected currency */
  formatPrice: (amount: number, fromCurrency?: Currency | string) => string;
  /** Get currency configuration */
  getCurrencyConfig: (currency?: Currency) => CurrencyConfig;
  /** Refresh exchange rates from API */
  refreshRates: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

/**
 * Hook to access currency context
 */
export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

interface CurrencyProviderProps {
  children: ReactNode;
}

/**
 * Currency provider component
 */
export function CurrencyProvider({ children }: CurrencyProviderProps): JSX.Element {
  const [currency, setCurrencyState] = useState<Currency>(Currency.GBP);
  const [exchangeRates, setExchangeRates] = useState<Record<Currency, number>>(FALLBACK_RATES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load saved currency preference and cached rates from localStorage
   */
  useEffect(() => {
    // Load saved currency preference
    const savedCurrency = localStorage.getItem(STORAGE_KEYS.currency) as Currency | null;
    if (savedCurrency && Object.values(Currency).includes(savedCurrency)) {
      setCurrencyState(savedCurrency);
    }

    // Load cached rates if still valid
    const cachedRates = localStorage.getItem(STORAGE_KEYS.rates);
    const cachedTimestamp = localStorage.getItem(STORAGE_KEYS.ratesTimestamp);

    if (cachedRates && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10);
      const age = Date.now() - timestamp;

      if (age < CACHE_TTL) {
        try {
          const rates = JSON.parse(cachedRates);
          setExchangeRates(rates);
          return; // Use cached rates, don't fetch
        } catch {
          // Invalid JSON, will fetch fresh rates
        }
      }
    }

    // Fetch fresh rates if no valid cache
    fetchExchangeRates();
  }, []);

  /**
   * Fetch exchange rates from API
   */
  const fetchExchangeRates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Using exchangerate-api.com free endpoint (no API key required)
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/GBP');

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      // Extract only the currencies we support
      const rates: Record<Currency, number> = {
        [Currency.GBP]: 1,
        [Currency.EUR]: data.rates.EUR || FALLBACK_RATES[Currency.EUR],
        [Currency.USD]: data.rates.USD || FALLBACK_RATES[Currency.USD],
        [Currency.CAD]: data.rates.CAD || FALLBACK_RATES[Currency.CAD],
        [Currency.AUD]: data.rates.AUD || FALLBACK_RATES[Currency.AUD],
      };

      setExchangeRates(rates);

      // Cache the rates
      localStorage.setItem(STORAGE_KEYS.rates, JSON.stringify(rates));
      localStorage.setItem(STORAGE_KEYS.ratesTimestamp, Date.now().toString());
    } catch (err) {
      console.error('Failed to fetch exchange rates:', err);
      setError('Failed to fetch exchange rates. Using fallback rates.');
      // Keep using fallback/cached rates
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Set currency and persist to localStorage
   */
  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem(STORAGE_KEYS.currency, newCurrency);
  }, []);

  /**
   * Normalize currency string to Currency enum
   */
  const normalizeCurrency = (currencyInput?: Currency | string): Currency => {
    if (!currencyInput) return Currency.GBP;
    const upperCurrency = String(currencyInput).toUpperCase() as Currency;
    // Check if it's a valid Currency enum value
    if (Object.values(Currency).includes(upperCurrency)) {
      return upperCurrency;
    }
    return Currency.GBP; // Default to GBP for unknown currencies
  };

  /**
   * Convert amount from source currency to selected currency
   * @param amount - The amount to convert
   * @param fromCurrency - Source currency (defaults to GBP, accepts string or Currency)
   */
  const convertPrice = useCallback(
    (amount: number, fromCurrency?: Currency | string): number => {
      const normalizedFrom = normalizeCurrency(fromCurrency);

      if (normalizedFrom === currency) {
        return amount;
      }

      // Convert to GBP first (if not already GBP)
      const amountInGBP = normalizedFrom === Currency.GBP
        ? amount
        : amount / exchangeRates[normalizedFrom];

      // Then convert from GBP to target currency
      const converted = amountInGBP * exchangeRates[currency];

      // Round to 2 decimal places
      return Math.round(converted * 100) / 100;
    },
    [currency, exchangeRates]
  );

  /**
   * Format price in selected currency
   * @param amount - The amount to format
   * @param fromCurrency - Source currency (defaults to GBP, accepts string or Currency)
   */
  const formatPrice = useCallback(
    (amount: number, fromCurrency?: Currency | string): string => {
      const normalizedFrom = normalizeCurrency(fromCurrency);
      const convertedAmount = convertPrice(amount, normalizedFrom);
      const config = CURRENCY_CONFIG[currency];

      // Use Intl.NumberFormat for proper locale-aware formatting
      const formatter = new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return formatter.format(convertedAmount);
    },
    [currency, convertPrice]
  );

  /**
   * Get currency configuration
   */
  const getCurrencyConfig = useCallback((targetCurrency?: Currency): CurrencyConfig => {
    return CURRENCY_CONFIG[targetCurrency || currency];
  }, [currency]);

  /**
   * Manual refresh of exchange rates
   */
  const refreshRates = useCallback(async () => {
    await fetchExchangeRates();
  }, [fetchExchangeRates]);

  const value: CurrencyContextValue = {
    currency,
    setCurrency,
    exchangeRates,
    loading,
    error,
    convertPrice,
    formatPrice,
    getCurrencyConfig,
    refreshRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
